import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { GraduationCap, Moon, Sun, LogOut, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

const linkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors hover:text-foreground ${
    isActive ? "text-foreground" : "text-muted-foreground"
  }`;

const mobileLinkClass = ({ isActive }) =>
  `block py-3 px-3 -mx-3 rounded-lg text-base font-medium transition-colors ${
    isActive ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted"
  }`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initial = (user?.name || user?.email || "P").slice(0, 1).toUpperCase();

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-border/70 bg-background/80 backdrop-blur-xl"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="navbar-brand">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <GraduationCap className="h-5 w-5" />
          </span>
          <span className="font-heading text-base sm:text-lg font-bold tracking-tight truncate">
            Tutors Paradise
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7">
          <NavLink to="/tutors" className={linkClass} data-testid="navbar-find-tutors-link">
            Find Tutors
          </NavLink>
          <NavLink to="/how-it-works" className={linkClass} data-testid="navbar-how-link">
            How It Works
          </NavLink>
          <NavLink to="/become-tutor" className={linkClass} data-testid="navbar-become-tutor-link">
            Become a Tutor
          </NavLink>
          {user && (
            <NavLink to="/dashboard" className={linkClass} data-testid="navbar-tutor-dashboard-link">
              Dashboard
            </NavLink>
          )}
          {user && (
            <NavLink to="/ai-practice" className={linkClass} data-testid="navbar-ai-practice-link">
              AI Practice
            </NavLink>
          )}
          {user && (
            <NavLink to="/certificates" className={linkClass} data-testid="navbar-certificates-link">
              Certificates
            </NavLink>
          )}
        </nav>

        {/* Right side actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label="Toggle theme"
            data-testid="theme-toggle-button"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {/* Desktop auth */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-10 w-10 rounded-full p-0 hidden md:inline-flex" data-testid="navbar-user-menu">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user.photo} alt={user.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {initial}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                className="hidden md:inline-flex"
                data-testid="navbar-login-button"
              >
                Log in
              </Button>
              <Button
                onClick={() => navigate("/register")}
                className="rounded-full hidden md:inline-flex"
                data-testid="navbar-auth-button"
              >
                Tutor Portal
              </Button>
            </>
          )}

          {/* Mobile hamburger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                aria-label="Open menu"
                data-testid="navbar-mobile-menu-button"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] max-w-sm p-0 flex flex-col" data-testid="navbar-mobile-menu">
              <SheetHeader className="p-5 border-b border-border text-left">
                <SheetTitle className="flex items-center gap-2 text-base">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                    <GraduationCap className="h-5 w-5" />
                  </span>
                  Tutors Paradise
                </SheetTitle>
              </SheetHeader>

              {user && (
                <div className="px-5 py-4 border-b border-border flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user.photo} alt={user.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {initial}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                </div>
              )}

              <nav className="flex-1 overflow-y-auto p-5 space-y-1">
                <Link to="/tutors" className="block py-3 px-3 -mx-3 rounded-lg text-base font-medium text-muted-foreground hover:bg-muted" onClick={() => setMobileOpen(false)} data-testid="navbar-mobile-find-tutors-link">
                  Find Tutors
                </Link>
                <Link to="/how-it-works" className="block py-3 px-3 -mx-3 rounded-lg text-base font-medium text-muted-foreground hover:bg-muted" onClick={() => setMobileOpen(false)}>
                  How It Works
                </Link>
                <Link to="/become-tutor" className="block py-3 px-3 -mx-3 rounded-lg text-base font-medium text-muted-foreground hover:bg-muted" onClick={() => setMobileOpen(false)}>
                  Become a Tutor
                </Link>
                {user && (
                  <>
                    <div className="pt-3 pb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Your account
                    </div>
                    <Link to="/dashboard" className="block py-3 px-3 -mx-3 rounded-lg text-base font-medium text-muted-foreground hover:bg-muted" onClick={() => setMobileOpen(false)}>
                      Dashboard
                    </Link>
                    <Link to="/ai-practice" className="block py-3 px-3 -mx-3 rounded-lg text-base font-medium text-muted-foreground hover:bg-muted" onClick={() => setMobileOpen(false)}>
                      AI Practice
                    </Link>
                    <Link to="/certificates" className="block py-3 px-3 -mx-3 rounded-lg text-base font-medium text-muted-foreground hover:bg-muted" onClick={() => setMobileOpen(false)}>
                      Certificates
                    </Link>
                  </>
                )}
              </nav>

              <div className="p-5 border-t border-border space-y-2">
                {user ? (
                  <Button
                    variant="outline"
                    onClick={handleLogout}
                    className="w-full rounded-full"
                    data-testid="navbar-mobile-logout-button"
                  >
                    <LogOut className="h-4 w-4 mr-1.5" /> Log out
                  </Button>
                ) : (
                  <>
                    <Button asChild variant="outline" className="w-full rounded-full">
                      <Link to="/login" onClick={() => setMobileOpen(false)} data-testid="navbar-mobile-login-button">Log in</Link>
                    </Button>
                    <Button asChild className="w-full rounded-full">
                      <Link to="/register" onClick={() => setMobileOpen(false)} data-testid="navbar-mobile-register-button">Tutor Portal</Link>
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
